import React from "react";
import {
  Eye, Wine, Music, Armchair, Camera, MapPin, Anchor, Ship,
  Sparkles, Sun, Moon, Building2, Clock, CheckCircle2, CalendarClock, CalendarDays,
} from "lucide-react";
import { Reveal, SectionHeading, SmartImage, PrimaryButton, SecondaryButton, openEnquiry } from "./common";
import { IMAGES, GALLERY, LANDMARKS, OCCASIONS, REASONS, CRUISE_DATES, CONTACT } from "../data";

const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

const KEYWORD_CHIPS = [
  "Vivid Cruises Sydney",
  "Vivid Cruises Sydney Harbour",
  "Vivid Cruise Sydney Harbour",
  "Cruise Vivid Sydney",
  "Vivid Cruise Sydney tickets",
  "Sydney Harbour Vivid Cruise",
  "Best Vivid Cruises Sydney",
  "Private Vivid Cruises Sydney",
  "Vivid Sydney Harbour Cruise",
  "Vivid Sydney Boat Cruise",
];

/* ---------------- Keyword rich intro ---------------- */
export const KeywordIntro = () => (
  <section id="vivid-cruises-sydney" data-testid="intro-section" className="relative py-20 sm:py-24">
    <div className="mx-auto max-w-5xl px-5 sm:px-8">
      <Reveal>
        <div className="text-center">
          <span className="text-sm uppercase tracking-[0.2em] text-vivid-cyan font-medium">Vivid Cruises Sydney Harbour</span>
          <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            The ultimate <span className="text-gradient-vivid">Vivid Cruise Sydney Harbour</span> experience
          </h2>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="mt-8 space-y-4 text-white/75 leading-relaxed">
          <p>
            Welcome to <strong className="text-white">Vivid Cruises Sydney</strong>, the home of the definitive
            <strong className="text-white"> Vivid Cruise Sydney Harbour</strong> experience. When you cruise Vivid Sydney
            with us, you sail the full length of the harbour during the world famous Vivid Sydney festival of light,
            music and ideas. A <strong className="text-white">Vivid Cruises Sydney Harbour</strong> departure is the
            single best way to see the festival, delivering front row, uninterrupted views of the illuminated Sydney
            Opera House, the glowing Sydney Harbour Bridge, Circular Quay, Barangaroo, Luna Park and Darling Harbour.
          </p>
          <p>
            Guests searching for <strong className="text-white">Vivid Cruise Sydney tickets</strong> choose us because
            no other <strong className="text-white">Sydney Harbour Vivid Cruise</strong> combines this level of comfort,
            value and vantage. Whether you want to <strong className="text-white">cruise Vivid Sydney</strong> with the
            family, book the <strong className="text-white">best Vivid Cruises Sydney</strong> has for a birthday, or
            arrange a <strong className="text-white">private Vivid cruise in Sydney</strong> for your team, every
            Vivid Cruises Sydney sailing is built to turn Sydney's biggest night into your best night.
          </p>
          <p>
            From the moment you step aboard at King Street Wharf, Wharf 3, a <strong className="text-white">Vivid Sydney Harbour
            cruise</strong> with Vivid Cruises Sydney is effortless: a fully licensed bar, spacious indoor and open air
            viewing decks, and expert local skippers who position you perfectly for every light installation. This is
            the Sydney Harbour boat cruise Vivid fans return to season after season.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {KEYWORD_CHIPS.map((k) => (
            <button
              key={k}
              onClick={() => go("#tickets")}
              className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-white/70 transition hover:border-vivid-magenta/50 hover:text-white"
            >
              {k}
            </button>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Why experience Vivid from the water ---------------- */
export const WhyWater = () => (
  <section id="why-water" data-testid="why-water-section" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <SectionHeading
            eyebrow="Why experience Vivid Sydney from the water"
            title="The best view of Vivid Sydney is from the harbour"
          />
          <div className="mt-6 space-y-4 text-white/75 leading-relaxed">
            <p>
              Every autumn, Sydney transforms into the largest festival of light, music and ideas in the
              Southern Hemisphere. While hundreds of thousands of visitors crowd the foreshore, the most
              spectacular way to experience it is with <strong className="text-white">Vivid Cruises Sydney</strong> on
              a Sydney Harbour cruise. From the deck you glide across the water with an ever changing, front row
              panorama of the illuminated city, with no jostling for position, no fences and no obstructed sightlines.
            </p>
            <p>
              A <strong className="text-white">Vivid cruise on Sydney Harbour</strong> reveals the festival exactly
              as it was designed to be seen. The Sydney Opera House sails become a giant canvas of colour, the
              Sydney Harbour Bridge pulses with light above you, and the reflections of Circular Quay, Barangaroo
              and Darling Harbour ripple across the black water. It is a perspective that simply cannot be matched
              from land, as the whole harbour becomes your amphitheatre.
            </p>
            <p>
              Choosing <strong className="text-white">Vivid Cruises Sydney</strong> also means comfort. Instead of
              standing in the cold for hours, you relax with a drink from the licensed bar, move freely between warm
              indoor lounges and open air viewing decks, and let the skipper navigate you to the very best vantage
              points. It is the difference between watching Vivid Sydney and truly experiencing it.
            </p>
            <p>
              Whether it is a romantic night for two, a family outing, a corporate event or a milestone celebration,
              a <strong className="text-white">Vivid Sydney boat cruise</strong> turns the festival into an
              unforgettable evening on the water, and Vivid Cruises Sydney makes it effortless.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <PrimaryButton onClick={() => go("#tickets")}>Book Your Vivid Cruise</PrimaryButton>
            <SecondaryButton onClick={() => go("#highlights")}>See the Highlights</SecondaryButton>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="grid grid-cols-2 gap-4">
            <SmartImage image={IMAGES.operaMagenta} caption className="col-span-2 h-64 rounded-3xl border border-white/10" />
            <SmartImage image={IMAGES.bridgeLights} className="h-48 rounded-2xl border border-white/10" />
            <SmartImage image={IMAGES.lightShowBoat} className="h-48 rounded-2xl border border-white/10" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Why choose Vivid Cruises Sydney + the vessel + features ---------------- */
const FEATURES = [
  { icon: Eye, title: "Spacious indoor & outdoor viewing areas", text: "Wander between the enclosed climate controlled saloon and generous open air decks for uninterrupted 360° views of every Vivid installation." },
  { icon: Wine, title: "Fully licensed bar", text: "A fully licensed onboard bar pours beer, wine, spirits and soft drinks all cruise long, so you can sip and watch the harbour glow." },
  { icon: Music, title: "Entertainment & atmosphere", text: "Curated music and a warm, celebratory atmosphere set the perfect tone as the Sydney skyline lights up around you." },
  { icon: Armchair, title: "Comfortable seating", text: "Relaxed, comfortable seating throughout means you can settle in and soak up the spectacle in total comfort." },
];

export const WhyMagic = () => (
  <section id="about" data-testid="about-section" className="relative py-24 sm:py-32 bg-vivid-surface/40 border-y border-white/5">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid items-start gap-12 lg:grid-cols-2">
        <Reveal>
          <SmartImage image={IMAGES.bar} caption className="h-[420px] rounded-3xl border border-white/10" />
        </Reveal>
        <Reveal delay={0.1}>
          <SectionHeading
            eyebrow="Why choose Vivid Cruises Sydney"
            title="Vivid Cruises Sydney, a premier Vivid harbour cruise"
          />
          <div className="mt-6 space-y-4 text-white/75 leading-relaxed">
            <p>
              <strong className="text-white">Vivid Cruises Sydney</strong> operates a beautifully appointed harbour
              vessel purpose built for nights exactly like these. Everything has been considered so that you can focus
              entirely on the lights, the atmosphere and the company you keep.
            </p>
            <p>
              The <strong className="text-white">Vivid Cruises Sydney</strong> experience blends space, comfort and
              genuine local expertise. Our skippers have navigated these waters through many Vivid seasons and know
              precisely where to be, and when, to catch each installation at its most breathtaking. That knowledge is
              what makes this one of the <strong className="text-white">best Vivid cruises in Sydney</strong>.
            </p>
            <p>
              From smooth, stable cruising to attentive service and thoughtful touches throughout, Vivid Cruises Sydney
              is designed to make your Vivid Sydney night seamless. Arrive at King Street Wharf, Wharf 3, step aboard, and let the
              harbour do the rest.
            </p>
          </div>
        </Reveal>
      </div>

      <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.08}>
            <div className="group h-full rounded-2xl border border-white/10 bg-black/40 p-6 transition-colors duration-500 hover:border-vivid-magenta/50">
              <f.icon className="h-8 w-8 text-vivid-cyan" />
              <h3 className="mt-4 text-lg font-semibold text-white">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/65">{f.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Highlights: landmarks + gallery + photography ---------------- */
export const Highlights = () => (
  <section id="highlights" data-testid="highlights-section" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <SectionHeading
          eyebrow="Cruise highlights"
          title="Front row seats to Sydney's most iconic Vivid installations"
          sub="Your Vivid Cruises Sydney harbour cruise sweeps past the festival's headline landmarks, the very icons that make Vivid Sydney world famous."
          center
        />
      </Reveal>

      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {GALLERY.map((img, i) => (
          <Reveal key={img.filename} delay={(i % 3) * 0.08}>
            <SmartImage image={img} caption className="h-64 rounded-2xl border border-white/10" />
          </Reveal>
        ))}
      </div>

      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {LANDMARKS.map((l, i) => (
          <Reveal key={l.name} delay={(i % 4) * 0.06}>
            <div className="flex h-full items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-5">
              <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-vivid-magenta" />
              <div>
                <h3 className="font-semibold text-white">{l.name}</h3>
                <p className="mt-1 text-sm text-white/60">{l.note}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Photography */}
      <div className="mt-16 grid items-center gap-10 lg:grid-cols-2">
        <Reveal>
          <SmartImage image={IMAGES.skylineSea} caption className="h-80 rounded-3xl border border-white/10" />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex items-center gap-3 text-vivid-cyan">
            <Camera className="h-6 w-6" />
            <span className="text-sm uppercase tracking-[0.2em]">Photography opportunities</span>
          </div>
          <h3 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">A photographer's dream on the water</h3>
          <div className="mt-5 space-y-4 text-white/75 leading-relaxed">
            <p>
              Few settings rival a <strong className="text-white">Vivid Cruises Sydney</strong> harbour cruise for night
              photography. The open decks give you clean, unobstructed angles of the illuminated Sydney Opera House, the
              glowing Sydney Harbour Bridge and the mirror like reflections of the city on the water, shots that are
              almost impossible to capture from the crowded foreshore.
            </p>
            <p>
              Whether you shoot on a professional camera or your phone, the constantly shifting perspective as you cruise
              the harbour delivers a portfolio of unforgettable images. Steady your camera on the rail, capture Circular
              Quay and Barangaroo aglow, and take home memories of Vivid Sydney that last long after the festival ends.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Cruise dates & sessions ---------------- */
export const CruiseDates = () => (
  <section id="dates" data-testid="dates-section" className="relative py-24 sm:py-32 bg-vivid-surface/40 border-y border-white/5">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <SectionHeading
          eyebrow="Vivid Sydney 2027 cruise dates"
          title="Vivid Cruises Sydney sailing dates & times"
          sub="Vivid Cruises Sydney runs evening harbour cruises from Friday 28 May to the festival finale on Saturday 19 June 2027. Choose your night and departure time, then book your tickets instantly."
          center
        />
      </Reveal>

      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {CRUISE_DATES.map((d, i) => (
          <Reveal key={d.iso} delay={(i % 4) * 0.05}>
            <div className={`relative h-full rounded-2xl border p-5 transition-colors duration-500 hover:border-vivid-magenta/60 ${d.finale ? "border-vivid-magenta/50 bg-vivid-magenta/5" : "border-white/10 bg-black/40"}`}>
              {d.finale && (
                <span className="absolute right-4 top-4 rounded-full bg-vivid-magenta px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">Finale</span>
              )}
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-4xl font-bold text-white">{d.day}</span>
                <div className="leading-tight">
                  <p className="font-semibold text-white">{d.month}</p>
                  <p className="text-xs text-white/50">{d.weekday} · {d.type}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {d.times.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-white/70">
                    <Clock className="h-3 w-3 text-vivid-cyan" /> {t}
                  </span>
                ))}
              </div>
              <button
                data-testid={`date-book-${d.iso}`}
                onClick={() => go("#tickets")}
                className="mt-5 w-full rounded-full border border-white/15 py-2 text-sm text-white/80 transition hover:bg-vivid-magenta hover:border-vivid-magenta hover:text-white"
              >
                Book this cruise
              </button>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-8 text-center text-sm text-white/50">Weeknight cruises depart at 6:00pm & 7:30pm. Weekend cruises add a 9:00pm sailing. All cruises depart King Street Wharf, Wharf 3, Sydney Harbour.</p>
    </div>
  </section>
);

/* ---------------- Occasions ---------------- */
const OCC_ICONS = { private: Anchor, corporate: Building2, birthday: Sparkles, family: Sun, group: Ship, romantic: Moon };

export const Occasions = () => (
  <section id="occasions" data-testid="occasions-section" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <SectionHeading
          eyebrow="Private charters, corporate, birthdays, family & groups"
          title="A Vivid cruise for every occasion"
          sub="However you like to celebrate, Vivid Cruises Sydney is one of the most versatile Vivid cruises in Sydney, from intimate private charters to large group bookings."
          center
        />
      </Reveal>

      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {OCCASIONS.map((o, i) => {
          const Icon = OCC_ICONS[o.key] || Anchor;
          return (
            <Reveal key={o.key} delay={(i % 3) * 0.08}>
              <div className="group h-full rounded-2xl border border-white/10 bg-black/40 p-7 transition-colors duration-500 hover:border-vivid-magenta/50">
                <Icon className="h-8 w-8 text-vivid-magenta" />
                <h3 className="mt-4 text-xl font-semibold text-white">{o.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/65">{o.blurb}</p>
                <button
                  data-testid={`occasion-enquire-${o.key}`}
                  onClick={() => openEnquiry(o.title)}
                  className="mt-4 text-sm font-medium text-vivid-cyan transition hover:text-white"
                >
                  Enquire now →
                </button>
              </div>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-12 space-y-4 text-white/75 leading-relaxed max-w-4xl">
          <p>
            A <strong className="text-white">private Vivid cruise in Sydney</strong> gives your group exclusive use of
            the vessel and a tailored route through the harbour's finest installations. It is the ultimate way to host a
            corporate Vivid event, impress interstate clients, or gather friends and family for a night no one will forget.
            <strong className="text-white"> Corporate Vivid cruises</strong> can be branded and catered to suit, while
            <strong className="text-white"> birthday cruises</strong>, <strong className="text-white">family cruises</strong> and
            <strong className="text-white"> group bookings</strong> enjoy flexible options and warm, personable service throughout.
          </p>
          <p>
            Whatever the celebration, our team will help you plan the perfect <strong className="text-white">Vivid Cruises
            Sydney</strong> experience. Send an enquiry with your group size and preferred date, and we'll take care of the details.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Departure, booking info, reasons to book early ---------------- */
export const Departure = () => (
  <section id="departure" data-testid="departure-section" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid gap-12 lg:grid-cols-2">
        <Reveal>
          <SectionHeading eyebrow="Departure & booking information" title="Everything you need to know before you board" />
          <div className="mt-6 space-y-5 text-white/75 leading-relaxed">
            <div className="flex gap-3">
              <MapPin className="mt-1 h-5 w-5 shrink-0 text-vivid-magenta" />
              <p><strong className="text-white">Departure point:</strong> Vivid Cruises Sydney departs from King Street Wharf, Wharf 3, in the heart of the Darling Harbour Vivid precinct and just steps from public transport, restaurants and the city. Your boarding time is confirmed with your booking. <a href={CONTACT.directions} target="_blank" rel="noopener noreferrer" data-testid="content-directions-link" className="text-vivid-cyan hover:underline">Get directions</a></p>
            </div>
            <div className="flex gap-3">
              <Clock className="mt-1 h-5 w-5 shrink-0 text-vivid-magenta" />
              <p><strong className="text-white">Duration & timing:</strong> Cruises run in the evening at 6:00pm, 7:30pm and 9:00pm on selected nights and last approximately 1.5 hours, timed to enjoy the installations at their most vivid. We recommend arriving 20 minutes before departure for a relaxed boarding.</p>
            </div>
            <div className="flex gap-3">
              <Anchor className="mt-1 h-5 w-5 shrink-0 text-vivid-magenta" />
              <p><strong className="text-white">The route:</strong> Cruise past the Sydney Opera House, beneath the Sydney Harbour Bridge, and around Circular Quay, Barangaroo, Luna Park at Milsons Point and Darling Harbour, the finest sweep of Vivid Sydney installations from the water.</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-vivid-magenta" />
              <p><strong className="text-white">What to bring:</strong> A warm jacket for the evening breeze, comfortable shoes, your camera or phone for photos, and your booking confirmation. The licensed bar takes care of refreshments on board.</p>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <PrimaryButton onClick={() => go("#tickets")}>Book Now</PrimaryButton>
            <SecondaryButton onClick={() => openEnquiry("General")}>Ask a Question</SecondaryButton>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-vivid-cyan/20 bg-vivid-surface/60 p-8">
            <div className="flex items-center gap-3 text-vivid-cyan">
              <CalendarClock className="h-6 w-6" />
              <span className="text-sm uppercase tracking-[0.2em]">Reasons to book early</span>
            </div>
            <h3 className="mt-4 text-2xl font-bold text-white">Vivid sailings sell out fast</h3>
            <p className="mt-3 text-white/70 leading-relaxed">
              Vivid Sydney is the busiest event on the harbour calendar, and evening cruises are the first to sell out.
              Booking your Vivid Cruises Sydney tickets early is the surest way to secure your preferred date and the best experience.
            </p>
            <div className="mt-6 space-y-4">
              {REASONS.map((r) => (
                <div key={r.title} className="rounded-xl border border-white/10 bg-black/30 p-4">
                  <h4 className="font-semibold text-white">{r.title}</h4>
                  <p className="mt-1 text-sm text-white/60">{r.text}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
